import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

interface Profile {
  is_activated: boolean;
  days_to_activation_bucket: string | null;
  lifetime_project_count: number;
  signup_event_sent: boolean;
  activation_event_sent: boolean;
  last_subscription_id_tracked: string | null;
  last_cancellation_id_tracked: string | null;
  welcome_modal_shown: boolean;
  signup_plan: string | null;
  signup_billing: string | null;
  paid_signup_checkout_initiated: boolean;
}

export const useProfile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const fetchProfile = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("is_activated, days_to_activation_bucket, lifetime_project_count, signup_event_sent, activation_event_sent, last_subscription_id_tracked, last_cancellation_id_tracked, welcome_modal_shown, signup_plan, signup_billing, paid_signup_checkout_initiated")
        .eq("id", user.id)
        .maybeSingle();

      setProfile(data);
      setIsLoading(false);
    };

    fetchProfile();
  }, [user]);

  const markSignupEventSent = useCallback(async () => {
    if (!user) return;
    
    const { error } = await supabase
      .from("profiles")
      .update({ signup_event_sent: true })
      .eq("id", user.id);

    if (error) {
      console.error("Failed to mark signup event sent:", error);
    } else {
      setProfile((prev) => prev ? { ...prev, signup_event_sent: true } : null);
    }
  }, [user]);

  const markActivationEventSent = useCallback(async () => {
    if (!user) return;
    
    const { error } = await supabase
      .from("profiles")
      .update({ activation_event_sent: true })
      .eq("id", user.id);

    if (error) {
      console.error("Failed to mark activation event sent:", error);
    } else {
      setProfile((prev) => prev ? { ...prev, activation_event_sent: true } : null);
    }
  }, [user]);

  const markSubscriptionTracked = useCallback(async (subscriptionId: string) => {
    if (!user) return;
    
    const { error } = await supabase
      .from("profiles")
      .update({ last_subscription_id_tracked: subscriptionId })
      .eq("id", user.id);

    if (error) {
      console.error("Failed to mark subscription tracked:", error);
    } else {
      setProfile((prev) => prev ? { ...prev, last_subscription_id_tracked: subscriptionId } : null);
    }
  }, [user]);

  const markCancellationTracked = useCallback(async (subscriptionId: string) => {
    if (!user) return;
    
    const { error } = await supabase
      .from("profiles")
      .update({ last_cancellation_id_tracked: subscriptionId })
      .eq("id", user.id);

    if (error) {
      console.error("Failed to mark cancellation tracked:", error);
    } else {
      setProfile((prev) => prev ? { ...prev, last_cancellation_id_tracked: subscriptionId } : null);
    }
  }, [user]);

  const markWelcomeModalShown = useCallback(async () => {
    if (!user) return;
    
    const { error } = await supabase
      .from("profiles")
      .update({ welcome_modal_shown: true })
      .eq("id", user.id);

    if (error) {
      console.error("Failed to mark welcome modal shown:", error);
    } else {
      setProfile((prev) => prev ? { ...prev, welcome_modal_shown: true } : null);
    }
  }, [user]);

  return { 
    profile, 
    isLoading, 
    markSignupEventSent, 
    markActivationEventSent, 
    markSubscriptionTracked, 
    markCancellationTracked,
    markWelcomeModalShown 
  };
};
